import { inject } from '@angular/core';
import { Route } from '@angular/router';
import { AppDefinition, AppDefinitionService, AppIdResolver } from '@processpuzzle/base-app';
import { catchError, firstValueFrom, map, of } from 'rxjs';

/**
 * Which of the organization's applications this shell is about — one rule, shared by the header's name
 * and by the runtime host, so the two cannot show different applications.
 *
 * **The published one wins.** An organization runs one application, but it can still hold two
 * definitions: the seeded `app`, empty, beside an installed starter's (`sailrc`). Preferring `app` by
 * name would show the empty one while the starter is the application actually in use — so a definition
 * that has been published comes first, the most recently changed if there are several. Before anything is
 * published `app` is preferred, then whichever comes first.
 */
export const SEEDED_APP_ID = 'app';

export function toApplicationList(response: AppDefinition[] | AppDefinition | { content: AppDefinition[] }): AppDefinition[] {
  if (Array.isArray(response)) return response;
  return 'content' in response ? response.content : [response];
}

export function currentApplicationOf(definitions: AppDefinition[]): AppDefinition | undefined {
  const published = definitions.filter((definition) => definition.publishedVersion != null).sort(byMostRecentlyUpdated);
  return published[0] ?? definitions.find((definition) => definition.id === SEEDED_APP_ID) ?? definitions[0];
}

/** ISO-8601 timestamps compare correctly as strings; a definition without one sorts last. */
function byMostRecentlyUpdated(first: AppDefinition, second: AppDefinition): number {
  return (second.updatedAt ?? '').localeCompare(first.updatedAt ?? '');
}

/**
 * The runtime host's {@link AppIdResolver}: lists the organization's applications and picks one by the rule
 * above. An empty collection arrives as an error rather than `[]`, which means no application either.
 */
export const currentApplicationId: AppIdResolver = () =>
  firstValueFrom(
    inject(AppDefinitionService)
      .findAll()
      .pipe(
        map((response) => currentApplicationOf(toApplicationList(response))?.id),
        catchError(() => of(undefined)),
      ),
  );

/**
 * The titled routes, in navigation order, wherever in the configuration they sit.
 *
 * Titled routes are the navigable ones. They are top-level, or children of a layout route — a route with
 * no path segment of its own, such as the workspace shell's — whose children share its URL level. The
 * application itself comes first, because it is what the organization's members came for.
 */
export function navigableRoutes(config: Route[]): Route[] {
  return config
    .flatMap((route) => (route.path === '' && route.children ? route.children : [route]))
    .filter((route) => route.title !== null && route.title !== undefined)
    .sort((first, second) => navOrderOf(first) - navOrderOf(second));
}

function navOrderOf(route: Route): number {
  return (route.data?.['navOrder'] as number | undefined) ?? 0;
}
